import { motion } from 'framer-motion';
import { Reveal, fadeUp, stagger } from './helpers';

const DELIVERABLES = [
    {
        title: 'Self-Insight',
        formal: 'Self-Insight Report',
        body: 'A map of how they think — the patterns in their own decisions, named back to them.',
    },
    {
        title: 'Identity',
        formal: 'Professional Identity',
        body: 'A profile of who they are professionally, based on the evidence of their own work.',
    },
    {
        title: 'Signature',
        formal: 'Knowledge Signature',
        body: 'The principles and rules of thumb they leave behind. A professional mark, not a corporate doc.',
    },
    {
        title: 'Opportunity',
        formal: 'Opportunity Layer',
        body: "Roles they're well-suited for next. Environments that fit. What makes them distinctive.",
    },
    {
        title: 'Legacy',
        formal: 'Legacy Report',
        body: 'A retrospective of what they built, decided, and left behind. A gift to keep, not a report to file.',
    },
];

export const Deliverables = () => (
    <section id="deliverables" className="py-32 scroll-mt-16 border-t border-border/40">
        <div className="max-w-6xl mx-auto px-6">
            <Reveal className="max-w-2xl mb-16">
                <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold mb-5">What the person walks away with</p>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.05]">
                    Five short documents.
                    <span className="text-muted-foreground/60"> Theirs to keep.</span>
                </h2>
                <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
                    Written from what they actually said in the interviews. In their voice, not corporate-speak. They can publish them, share them, or just keep them.
                </p>
            </Reveal>

            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                {DELIVERABLES.map((d, i) => (
                    <motion.div
                        key={d.title}
                        variants={fadeUp}
                        whileHover={{ y: -6, transition: { duration: 0.25 } }}
                        className="rounded-2xl border border-border/60 bg-card p-6 flex flex-col gap-3 cursor-default group relative overflow-hidden"
                    >
                        <motion.div
                            className="absolute inset-0 -z-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{
                                background: 'radial-gradient(circle at 50% 0%, hsl(221 83% 53% / 0.10) 0%, transparent 70%)',
                            }}
                        />
                        <div className="flex items-center justify-between relative">
                            <span className="text-2xl font-bold tabular-nums text-muted-foreground/40 tracking-tight group-hover:text-primary/60 transition-colors">
                                {String(i + 1).padStart(2, '0')}
                            </span>
                        </div>
                        <div className="relative">
                            <h3 className="text-base font-semibold tracking-tight leading-snug">{d.title}</h3>
                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5 font-medium">{d.formal}</p>
                        </div>
                        <p className="text-[13px] text-muted-foreground leading-relaxed relative">{d.body}</p>
                    </motion.div>
                ))}
            </motion.div>
        </div>
    </section>
);
